import type { ClientMessage, ErrorMsg, ServerMessage } from '@shared/protocol/messages';
import { Emitter } from '../game/core/Emitter';
import type { ApiClient } from './ApiClient';
import { t } from '../i18n';

export type ConnState = 'offline' | 'connecting' | 'online' | 'reconnecting';

type DistributiveOmit<T, K extends keyof never> = T extends unknown ? Omit<T, K> : never;
export type Outgoing = DistributiveOmit<ClientMessage, 'id'>;

type ServerEvents = { [M in ServerMessage as M['t']]: M } & { state: ConnState; replaced: string };

interface Pending {
  resolve: () => void;
  reject: (e: ErrorMsg) => void;
  timer: number;
}

/**
 * WebSocket client with auth handshake, request ids (acks/dedupe on the
 * server), heartbeat and automatic reconnect with exponential backoff. The
 * server re-attaches us to our room after a reconnect.
 */
export class MultiplayerClient extends Emitter<ServerEvents> {
  state: ConnState = 'offline';
  private ws: WebSocket | null = null;
  private token: string | null = null;
  private seq = 0;
  private pending = new Map<string, Pending>();
  private attempts = 0;
  private reconnectTimer = 0;
  private pingTimer = 0;
  private wantOpen = false;
  private authed: (() => void) | null = null;
  /** The handshake in progress, shared by concurrent connect() calls. */
  private opening: Promise<void> | null = null;

  constructor(private readonly api: ApiClient) {
    super();
  }

  /**
   * Open the socket (or reuse the open one). Safe to call from several places
   * at once: a second socket would replace the first on the server.
   */
  connect(token: string): Promise<void> {
    this.token = token;
    this.wantOpen = true;
    if (this.ws && this.state === 'online') return Promise.resolve();
    if (this.opening) return this.opening;
    window.clearTimeout(this.reconnectTimer);
    return this.open();
  }

  private open(): Promise<void> {
    const p = this.handshake();
    this.opening = p;
    p.then(
      () => this.opening === p && (this.opening = null),
      () => this.opening === p && (this.opening = null),
    );
    return p;
  }

  private handshake(): Promise<void> {
    this.setState(this.attempts > 0 ? 'reconnecting' : 'connecting');
    return new Promise((resolve, reject) => {
      let settled = false;
      const ws = new WebSocket(this.api.wsUrl);
      this.ws = ws;
      const fail = (why: string): void => {
        if (!settled) {
          settled = true;
          reject(new Error(why));
        }
      };
      ws.onopen = () => {
        this.authed = () => {
          settled = true;
          this.attempts = 0;
          this.setState('online');
          resolve();
        };
        ws.send(JSON.stringify({ t: 'auth', id: this.nextId(), token: this.token }));
      };
      ws.onmessage = (ev) => this.onMessage(ev.data);
      ws.onerror = () => fail(t('Could not connect to the battle server.'));
      ws.onclose = (ev) => {
        // 4000 = this player connected from another tab; 1008 = auth/policy failure.
        // Reconnecting would just fight the other tab (or fail again), so stop.
        if (ev.code === 4000 || ev.code === 1008) {
          this.wantOpen = false;
          this.emit('replaced', ev.code === 4000 ? t('Opened in another tab — this tab is now offline.') : t('The server closed the connection.'));
        }
        fail(t('Connection closed.'));
        this.ws = null;
        window.clearInterval(this.pingTimer);
        for (const [id, p] of this.pending) {
          window.clearTimeout(p.timer);
          p.reject({ t: 'error', id, code: 'internal', message: t('Connection lost.') });
        }
        this.pending.clear();
        if (this.wantOpen) this.scheduleReconnect();
        else this.setState('offline');
      };
    });
  }

  private scheduleReconnect(): void {
    this.setState('reconnecting');
    const delay = Math.min(10000, 500 * Math.pow(2, this.attempts++)) * (0.75 + Math.random() * 0.5);
    window.clearTimeout(this.reconnectTimer);
    this.reconnectTimer = window.setTimeout(() => {
      this.open().catch(() => {
        /* onclose schedules the next attempt */
      });
    }, delay);
  }

  disconnect(): void {
    this.wantOpen = false;
    window.clearTimeout(this.reconnectTimer);
    window.clearInterval(this.pingTimer);
    this.ws?.close(1000, 'bye');
    this.ws = null;
    this.setState('offline');
  }

  private setState(s: ConnState): void {
    if (this.state === s) return;
    this.state = s;
    this.emit('state', s);
  }

  private nextId(): string {
    return `c${++this.seq}`;
  }

  /** Send a request; resolves on ack, rejects with the server's error message. */
  send(msg: Outgoing, timeoutMs = 8000): Promise<void> {
    const ws = this.ws;
    if (!ws || ws.readyState !== WebSocket.OPEN || this.state !== 'online') {
      return Promise.reject<void>({ t: 'error', code: 'internal', message: t('Not connected to the battle server.') } satisfies ErrorMsg);
    }
    const id = this.nextId();
    return new Promise<void>((resolve, reject) => {
      const timer = window.setTimeout(() => {
        this.pending.delete(id);
        reject({ t: 'error', id, code: 'internal', message: t('The server did not answer in time.') } satisfies ErrorMsg);
      }, timeoutMs);
      this.pending.set(id, { resolve, reject, timer });
      ws.send(JSON.stringify({ ...msg, id }));
    });
  }

  private onMessage(raw: unknown): void {
    if (typeof raw !== 'string') return;
    let msg: ServerMessage;
    try {
      msg = JSON.parse(raw) as ServerMessage;
    } catch {
      return;
    }
    if (msg.t === 'ack' || (msg.t === 'error' && msg.id)) {
      const p = this.pending.get(msg.id ?? '');
      if (p) {
        this.pending.delete(msg.id ?? '');
        window.clearTimeout(p.timer);
        if (msg.t === 'ack') p.resolve();
        else p.reject(msg);
      }
    }
    if (msg.t === 'error' && msg.code === 'unauthorized') {
      // Token rejected: stop reconnecting; the UI will re-register.
      this.wantOpen = false;
    }
    if (msg.t === 'welcome') {
      this.authed?.();
      this.authed = null;
      window.clearInterval(this.pingTimer);
      this.pingTimer = window.setInterval(() => {
        this.send({ t: 'ping' }).catch(() => {});
      }, 15000);
    }
    this.emit(msg.t, msg as never);
  }
}
